/**
 * Smart Scroll X-Ray: the schedule, drawn over the song it was built for.
 *
 * The Part Timer (timingRecorder.js) answers "was the estimate right?" after the fact. This answers
 * the question you have while looking at a song that scrolls wrong: *why is it going at this speed
 * here?* Every part on screen gets a tinted box and a chip reading out the chain that set its pace —
 * which branch of the estimator was used, how many bars that gave it, what the schedule turned that
 * into in seconds and pixels, and how the result compares with the song's own average velocity.
 *
 * It is a second calibration instrument, so it lives behind the same build flag as the first: never
 * in a production bundle, off by default in development, switched on from the Auto Scroll settings
 * modal. Nothing here changes the schedule — it only reports what buildSchedule already decided.
 */

import { getSchedule, measureRenderedParts } from './smartScrollSchedule';
import { estimatePartDuration, getBeatsPerBar, getSecondsPerBar } from './songTiming';
import { describeEstimate, isCalibrationUser } from './timingRecorder';
import { getSetting, settingKeys } from './settings';

/** Fired by the settings modal so an open View picks the toggle up without a remount. */
export const SMART_SCROLL_DEBUG_CHANGED_EVENT = 'smartScrollDebugChanged';

/* ---------------------------------------------------------------------------
 * The part being timed
 *
 * A Part Timer run and the scroll engine each have an opinion about which part is playing: the
 * player's taps, and the schedule's clock. The overlay marks both, because the gap between them is
 * the whole reading — the engine sitting a part ahead of the taps is the estimate running short,
 * seen while it happens rather than worked out from an export afterwards.
 *
 * A module slot rather than a prop: the recorder and the overlay are siblings under View, and this
 * is a development instrument talking to a development instrument.
 * ------------------------------------------------------------------------- */

export const TIMED_PART_CHANGED_EVENT = 'smartScrollDebugTimedPart';

let timedPartId = null;

export const setTimedPart = (partId) => {
  if (timedPartId === partId) return;
  timedPartId = partId || null;
  window.dispatchEvent(new Event(TIMED_PART_CHANGED_EVENT));
};

export const getTimedPart = () => timedPartId;

// Same two gates as the Part Timer — a development build, and an admin looking at it
export const isSmartScrollDebugEnabled = () =>
  isCalibrationUser() && Boolean(getSetting(settingKeys.smartScrollDebug));

/**
 * How far from the song's average velocity a part has to be before it is worth a colour.
 *
 * Every part differs from the average a little — the curve is smooth, so neighbours bleed into each
 * other. Below this the box stays neutral, which keeps the colour meaning "this part is deliberately
 * quicker" rather than "this part is a rounding error away from the mean".
 */
const NEUTRAL_BAND = 0.1;

/**
 * A part's velocity relative to the song, as a colour.
 *
 * Diverging, because the reading is a signed departure from a meaningful midpoint: warm for faster
 * than the song's average, cool for slower, and desaturated at the average itself. The magnitude is
 * taken in log space so that 2x faster and 2x slower are the same distance from neutral — a ratio
 * scale read linearly would make everything slower than average look identical.
 */
export const velocityTint = (ratio) => {
  if (!(ratio > 0)) return { hue: 220, strength: 0 };

  const magnitude = Math.min(1, Math.abs(Math.log2(ratio)));
  if (magnitude < NEUTRAL_BAND) return { hue: 220, strength: 0 };

  return { hue: ratio > 1 ? 12 : 205, strength: magnitude };
};

/** Why a part is the length it is, in the words the estimator would use. */
const SOURCE_LABELS = {
  // Distinct from 'chord bars' below on purpose: this is the chart stating its bars, that is a
  // count of the chords written on the page
  chart: 'stored chart',
  directive: 'timed {Nx,s}',
  lyrics: 'lyric lines',
  chords: 'chord bars',
  default: 'fallback bars',
  section: 'divider',
};

/**
 * The rows to draw, one per rendered part, plus the song-level decisions they all sit inside.
 *
 * Measured from the same DOM query the scheduler uses, and lined up with its breakpoints by index,
 * so what is drawn is the schedule that is installed — not a re-derivation that could quietly
 * disagree with it. Returns an empty list when Smart Scroll is off: there is no schedule then, and
 * inventing one to draw would be showing a pace the app is not using.
 */
export const buildDebugRows = (songs = []) => {
  const schedule = getSchedule();
  if (!schedule) return { rows: [], schedule: null };

  const measured = measureRenderedParts();
  if (!measured.length) return { rows: [], schedule: null };

  const byId = {};
  songs.forEach((song) => {
    if (!song?._id) return;
    const secondsPerBar = getSecondsPerBar(song);
    (song.parts || []).forEach((part) => {
      byId[part.id] = { song, part, secondsPerBar };
    });
  });

  const breakpoints = schedule.breakpoints || [];
  const average = schedule.averageVelocity || 0;

  const rows = measured.map((node, index) => {
    const entry = byId[node.partId];
    const part = entry?.part;

    const from = breakpoints[index];
    const to = breakpoints[index + 1];
    // A breakpoint list that has drifted out of step with the DOM (a rebuild mid-render) would
    // otherwise label every part with its neighbour's numbers, which is worse than labelling none
    const aligned = from && to && from.partId === node.partId;

    const startTime = aligned ? from.time : null;
    const endTime = aligned ? to.time : null;
    const seconds = aligned ? to.time - from.time : null;
    const px = aligned ? to.position - from.position : null;
    const pxPerSecond = aligned && seconds > 0 ? px / seconds : 0;
    const ratio = average > 0 && aligned ? pxPerSecond / average : 1;

    const beatsPerBar = getBeatsPerBar(entry?.song?.timeSignature);
    const estimate = part ? describeEstimate(part, beatsPerBar) : {};
    const duration = part ? estimatePartDuration(part, entry.secondsPerBar, beatsPerBar) : null;
    const repeatCount = Number(part?.repeatCount) > 1 ? Number(part.repeatCount) : 1;

    return {
      partId: node.partId,
      top: node.top,
      height: node.bottom - node.top,
      name: part ? `${part.name} ${part.number ?? ''}`.trim() : node.partId,

      // What the estimator did
      source: estimate.source || null,
      sourceLabel: SOURCE_LABELS[estimate.source] || estimate.source || 'unknown',
      lyricLines: estimate.lyricLines ?? 0,
      chordLines: estimate.chordLines ?? 0,
      barsPerPass: estimate.barsPerPass ?? 0,
      repeatCount,
      secondsPerBar: entry?.secondsPerBar ?? null,
      predictedSeconds: duration?.seconds ?? null,
      predictedBars: duration?.bars ?? null,
      exact: Boolean(duration?.exact),

      // What the schedule made of it
      startTime,
      endTime,
      seconds,
      px,
      pxPerSecond,
      ratio,
      // The three ways the schedule overrides the estimate, each of which makes the part's pace a
      // decision of the scheduler's rather than of the music's
      capped: Boolean(from?.capped),
      flattened: Boolean(from?.flattened),
      held: px != null && px < 1,
      // Read off the breakpoint rather than off duration.measured: the estimator says the chart
      // stated this part's length, the schedule says whether it is still being played at it
      measured: Boolean(from?.measured),
    };
  });

  return { rows, schedule };
};

/**
 * Which row the scroll engine is inside at a given schedule time.
 *
 * Read from the engine's own clock rather than from the scroll position: the two agree, but time is
 * what the schedule is a function of, and a part that is being held on (no pixels of its own) has
 * no position range to look itself up by.
 */
export const findActiveRow = (rows, time) => {
  if (!(time >= 0)) return -1;
  return rows.findIndex((row) => row.startTime != null && time >= row.startTime && time < row.endTime);
};
